import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, CheckCircle2, XCircle, Home, RefreshCw, MessageCircle, Download, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { printReceiptSmart } from "../lib/btPrinter";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentStatus() {
  const { paymentId } = useParams();
  const [payment, setPayment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const receiptRef = useRef(null);

  useEffect(() => {
    fetchPaymentStatus();
    const interval = setInterval(fetchPaymentStatus, 10000);
    return () => clearInterval(interval);
  }, [paymentId]);

  const fetchPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/customer/payment-status/${paymentId}`);
      setPayment(response.data);
    } catch (err) {
      console.error("Failed to fetch payment status");
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get bills detail from payment (new format) or fallback to old format
  const getBillsDetail = () => {
    if (payment?.bills_detail && payment.bills_detail.length > 0) {
      return payment.bills_detail;
    }
    // Fallback for old payments without bills_detail
    const periodeArray = payment?.periode_list ? payment.periode_list.split(', ') : [];
    const jumlahPerBill = periodeArray.length > 0 ? Math.floor(payment.jumlah / periodeArray.length) : payment?.jumlah || 0;
    const adminFeePerBill = periodeArray.length > 0 ? Math.floor(payment.admin_fee / periodeArray.length) : payment?.admin_fee || 0;
    
    return periodeArray.map(periode => ({
      bulan: periode,
      harga_air: jumlahPerBill,
      adm: 0,
      denda: 0,
      jumlah: jumlahPerBill,
      admin_fee: adminFeePerBill,
      total: jumlahPerBill + adminFeePerBill
    }));
  };

  // Generate plain text receipt for thermal printer via RawBT (58mm format)
  const generateReceiptText = () => {
    if (!payment) return "";
    
    const divider = "--------------------------------";
    const formatRp = (n) => `Rp${n?.toLocaleString('id-ID') || '0'}`;
    const billsDetail = getBillsDetail();
    
    let receipt = "";
    receipt += "     PRODUK DIGITAL\n";
    receipt += "     Struk Pembayaran\n";
    receipt += divider + "\n";
    receipt += `Pelanggan: ${payment.nama}\n`;
    receipt += `${payment.alamat || 'TEBING TINGGI'}\n`;
    receipt += `No.Samb  : ${payment.no_samb}\n`;
    receipt += divider + "\n";
    
    // List each bill with full details
    billsDetail.forEach((bill) => {
      receipt += `Periode  : ${bill.bulan}\n`;
      receipt += `Denda    : ${formatRp(bill.denda)}\n`;
      receipt += `Harga Air: ${formatRp(bill.harga_air)}\n`;
      receipt += `Adm      : ${formatRp(bill.adm)}\n`;
      receipt += `Biaya Adm: ${formatRp(bill.admin_fee)}\n`;
      receipt += `SUBTOTAL : ${formatRp(bill.total)}\n`;
      receipt += divider + "\n";
    });
    
    receipt += `TOTAL BAYAR  ${formatRp(payment.total_bayar)}\n`;
    receipt += divider + "\n";
    receipt += `Status: LUNAS\n`;
    receipt += divider + "\n";
    receipt += "  Terima kasih atas pembayaran\n";
    receipt += "            Anda\n";
    receipt += " Struk ini adalah bukti\n";
    receipt += "    pembayaran yang sah\n";
    receipt += "\n\n\n";
    
    return receipt;
  };

  // Print langsung via Web Bluetooth (fallback RawBT)
  const handlePrintRawBT = async () => {
    if (!payment) return;
    await printReceiptSmart(generateReceiptText(), toast);
  };

  const handlePrintReceipt = () => {
    // Optimized for 58mm thermal printer (format seperti foto tagihan dengan detail lengkap)
    const billsDetail = getBillsDetail();
    
    // Generate bill items HTML with full details
    const billItemsHtml = billsDetail.map((bill) => {
      return `<div class="bill-item">
        <div class="bill-header">
          <span class="nama">${payment.nama}</span>
          <span class="status">Belum Bayar</span>
        </div>
        <div class="alamat">${payment.alamat || 'TEBING TINGGI'}</div>
        <div class="detail-row">
          <span class="label">Periode</span>
          <span class="label">Denda</span>
        </div>
        <div class="detail-row">
          <span class="value">${bill.bulan}</span>
          <span class="value denda">Rp${(bill.denda || 0).toLocaleString('id-ID')}</span>
        </div>
        <div class="detail-small">Harga Air: Rp${(bill.harga_air || 0).toLocaleString('id-ID')}</div>
        <div class="detail-small">Adm: Rp${(bill.adm || 0).toLocaleString('id-ID')}</div>
        <div class="detail-small fee">Biaya Admin: Rp${(bill.admin_fee || 0).toLocaleString('id-ID')}</div>
        <div class="bill-total">Subtotal: Rp${(bill.total || 0).toLocaleString('id-ID')}</div>
      </div>`;
    }).join('');
    
    const printWindow = window.open('', '', 'width=300,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Struk Pembayaran</title>
          <style>
            @page { 
              size: 58mm auto;
              margin: 0;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body { 
              font-family: 'Courier New', Courier, monospace; 
              width: 58mm;
              padding: 2mm;
              font-size: 10px;
              line-height: 1.4;
            }
            .header { 
              text-align: center; 
              padding-bottom: 2mm;
              border-bottom: 1px dashed #000;
              margin-bottom: 2mm;
            }
            .logo { font-size: 14px; font-weight: bold; }
            .title { font-size: 12px; margin-top: 1mm; }
            .customer-info {
              margin-bottom: 2mm;
              padding-bottom: 2mm;
              border-bottom: 1px dashed #000;
            }
            .bill-item {
              border: 1px solid #ccc;
              border-radius: 4px;
              padding: 2mm;
              margin-bottom: 2mm;
              background: #f9f9f9;
            }
            .bill-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 1mm;
            }
            .nama { font-weight: bold; font-size: 10px; }
            .alamat { font-size: 9px; color: #666; margin-bottom: 2mm; }
            .status { 
              background: #fee; 
              color: #c00; 
              padding: 1px 4px; 
              font-size: 8px;
              border-radius: 2px;
            }
            .detail-row {
              display: flex;
              justify-content: space-between;
              font-size: 9px;
            }
            .label { color: #666; }
            .value { font-weight: bold; }
            .denda { color: #c00; }
            .detail-small { font-size: 9px; color: #666; }
            .fee { color: #0066cc; font-weight: bold; }
            .bill-total { 
              text-align: right; 
              font-size: 12px; 
              font-weight: bold; 
              color: #0066cc;
              margin-top: 2mm;
            }
            .divider { 
              border-top: 1px dashed #000; 
              margin: 2mm 0; 
            }
            .total-section {
              background: #f0f7ff;
              border: 1px solid #0066cc;
              border-radius: 4px;
              padding: 3mm;
              margin-top: 2mm;
            }
            .total-row { 
              display: flex; 
              justify-content: space-between; 
              font-size: 11px; 
              font-weight: bold;
            }
            .total-amount { 
              font-size: 14px; 
              color: #0066cc;
            }
            .footer { 
              text-align: center; 
              margin-top: 3mm; 
              font-size: 9px;
              border-top: 1px dashed #000;
              padding-top: 2mm;
            }
            @media print {
              body { width: 58mm; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">PRODUK DIGITAL</div>
            <div class="title">Struk Pembayaran</div>
          </div>
          
          <div class="customer-info">
            <div>ID Pelanggan: ${payment.no_samb}</div>
          </div>
          
          ${billItemsHtml}
          
          <div class="total-section">
            <div class="total-row">
              <span>Total Bayar</span>
              <span class="total-amount">Rp${payment.total_bayar.toLocaleString('id-ID')}</span>
            </div>
            <div style="font-size:8px;color:#666;margin-top:1mm;">
              Sudah termasuk biaya admin
            </div>
          </div>
          
          <div class="divider"></div>
          
          <div class="detail-row" style="padding:1mm 0;">
            <span>Status:</span>
            <span style="background:#dfd;color:#060;padding:1px 4px;border-radius:2px;font-weight:bold">LUNAS ✓</span>
          </div>
          
          <div class="footer">
            <p>Terima kasih</p>
            <p>Bukti pembayaran sah</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleDownloadReceipt = async () => {
    // Create canvas for PNG receipt (format sesuai tagihan: detail per bulan)
    const billsDetail = getBillsDetail();
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Calculate dynamic height based on number of bills
    // Each bill card needs more space for details
    const headerHeight = 120;
    const billCardHeight = 180; // Height per bill card with full details
    const footerHeight = 160;
    const dynamicHeight = headerHeight + (billsDetail.length * billCardHeight) + footerHeight;
    
    const width = 400;
    const height = dynamicHeight;
    canvas.width = width;
    canvas.height = height;
    
    // Background
    ctx.fillStyle = '#f0f7ff';
    ctx.fillRect(0, 0, width, height);
    
    // Header background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, 100);
    
    // Header text
    ctx.fillStyle = '#1e293b';
    ctx.font = 'bold 20px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Tagihan Pelanggan', width / 2, 40);
    
    ctx.font = '13px Arial';
    ctx.fillStyle = '#0ea5e9';
    ctx.fillText(`ID Pelanggan: ${payment.no_samb}`, width / 2, 65);
    
    let y = 110;
    
    // Draw each bill card
    billsDetail.forEach((bill, index) => {
      // Card background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(20, y, width - 40, billCardHeight - 15);
      
      // Card border
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 1;
      ctx.strokeRect(20, y, width - 40, billCardHeight - 15);
      
      const cardY = y + 20;
      
      // Name and status
      ctx.fillStyle = '#1e293b';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(payment.nama, 35, cardY);
      
      // Status badge "Belum Bayar" -> now "LUNAS"
      ctx.fillStyle = '#dcfce7';
      ctx.fillRect(width - 100, cardY - 12, 70, 18);
      ctx.fillStyle = '#15803d';
      ctx.font = 'bold 10px Arial';
      ctx.textAlign = 'right';
      ctx.fillText('LUNAS ✓', width - 35, cardY);
      
      // Address
      ctx.fillStyle = '#64748b';
      ctx.font = '11px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(payment.alamat || 'TEBING TINGGI', 35, cardY + 18);
      
      // Periode and Denda labels
      ctx.fillStyle = '#94a3b8';
      ctx.font = '10px Arial';
      ctx.fillText('Periode', 35, cardY + 40);
      ctx.fillText('Denda', width - 120, cardY + 40);
      
      // Periode and Denda values
      ctx.fillStyle = '#1e293b';
      ctx.font = 'bold 12px Arial';
      ctx.fillText(bill.bulan, 35, cardY + 55);
      ctx.fillStyle = '#ef4444';
      ctx.fillText(`Rp${(bill.denda || 0).toLocaleString('id-ID')}`, width - 120, cardY + 55);
      
      // Details (Harga Air, Adm, Biaya Admin)
      ctx.fillStyle = '#64748b';
      ctx.font = '10px Arial';
      ctx.fillText(`Harga Air: Rp${(bill.harga_air || 0).toLocaleString('id-ID')}`, 35, cardY + 75);
      ctx.fillText(`Adm: Rp${(bill.adm || 0).toLocaleString('id-ID')}`, 35, cardY + 90);
      ctx.fillStyle = '#0ea5e9';
      ctx.font = 'bold 10px Arial';
      ctx.fillText(`Biaya Admin: Rp${(bill.admin_fee || 0).toLocaleString('id-ID')}`, 35, cardY + 105);
      
      // Subtotal
      ctx.fillStyle = '#64748b';
      ctx.font = '10px Arial';
      ctx.textAlign = 'right';
      ctx.fillText('Subtotal', width - 35, cardY + 80);
      ctx.fillStyle = '#0ea5e9';
      ctx.font = 'bold 16px Arial';
      ctx.fillText(`Rp${(bill.total || 0).toLocaleString('id-ID')}`, width - 35, cardY + 100);
      
      y += billCardHeight;
    });
    
    // Total section
    y += 10;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(20, y, width - 40, 70);
    ctx.strokeStyle = '#0ea5e9';
    ctx.lineWidth = 2;
    ctx.strokeRect(20, y, width - 40, 70);
    
    ctx.fillStyle = '#1e293b';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'left';
    ctx.fillText('Total Bayar', 40, y + 30);
    
    ctx.fillStyle = '#0ea5e9';
    ctx.font = 'bold 22px Arial';
    ctx.textAlign = 'right';
    ctx.fillText(`Rp${payment.total_bayar.toLocaleString('id-ID')}`, width - 40, y + 35);
    
    ctx.fillStyle = '#94a3b8';
    ctx.font = '10px Arial';
    ctx.textAlign = 'left';
    ctx.fillText('Sudah termasuk biaya admin', 40, y + 55);
    
    y += 90;
    
    // Footer
    ctx.fillStyle = '#94a3b8';
    ctx.font = '11px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Terima kasih atas pembayaran Anda', width / 2, y);
    ctx.fillText('Struk ini adalah bukti pembayaran yang sah', width / 2, y + 18);
    
    // Download as PNG
    try {
      canvas.toBlob((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Struk_${payment.id.slice(0, 8).toUpperCase()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success("Struk berhasil diunduh");
      }, 'image/png');
    } catch (error) {
      toast.error("Gagal mengunduh struk");
    }
  };

  const StatusDisplay = () => {
    if (!payment) return null;

    switch (payment.status) {
      case "pending":
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-amber-600 animate-pulse" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">
              Menunggu Verifikasi
            </h2>
            <p className="text-slate-600 mb-6">
              Pembayaran Anda sedang diverifikasi oleh admin. Proses ini biasanya membutuhkan waktu 1-24 jam.
            </p>
            <div className="glass-card-solid rounded-xl p-4 text-left mb-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">ID Pembayaran</span>
                  <span className="font-mono text-slate-800">{payment.id.slice(0, 8)}...</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Pelanggan</span>
                  <span className="text-slate-800">{payment.nama}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Periode</span>
                  <span className="text-slate-800">{payment.periode_list}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Total</span>
                  <span className="font-semibold text-sky-600">{formatCurrency(payment.total_bayar)}</span>
                </div>
              </div>
            </div>
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 text-left">
              <p className="text-sm text-sky-800 mb-2">
                <strong>Butuh bantuan?</strong>
              </p>
              <p className="text-sm text-sky-700 mb-3">
                Jika pembayaran belum disetujui dalam 24 jam, silakan hubungi CS kami:
              </p>
              <a 
                href="https://wa.me/6289512168007" 
                target="_blank" 
                rel="noopener noreferrer"
                className="wa-button inline-flex"
              >
                <MessageCircle className="w-5 h-5" />
                089512168007
              </a>
            </div>
          </div>
        );
      
      case "confirmed":
        // Get bills detail
        const billsDetail = getBillsDetail();
        
        return (
          <div className="text-center" ref={receiptRef}>
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">
              Pembayaran Berhasil!
            </h2>
            <p className="text-slate-600 mb-6">
              Terima kasih! Pembayaran Anda telah dikonfirmasi.
            </p>
            
            {/* Receipt Card - Format sesuai tampilan tagihan */}
            <div className="bg-gradient-to-b from-sky-50 to-white border border-slate-200 rounded-xl overflow-hidden text-left mb-6">
              {/* Header */}
              <div className="bg-white p-4 border-b border-slate-100">
                <h3 className="font-heading font-bold text-slate-800 text-center text-lg">Tagihan Pelanggan</h3>
                <p className="text-center text-sm text-sky-600">ID Pelanggan: {payment.no_samb}</p>
              </div>
              
              {/* Bill Cards */}
              <div className="p-4 space-y-3">
                {billsDetail.map((bill, idx) => (
                  <div key={idx} className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-slate-800">{payment.nama}</p>
                        <p className="text-xs text-slate-500">{payment.alamat || 'TEBING TINGGI'}</p>
                      </div>
                      <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-xs font-medium">
                        LUNAS ✓
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div>
                        <p className="text-xs text-slate-400">Periode</p>
                        <p className="font-medium text-slate-800 text-sm">{bill.bulan}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400">Denda</p>
                        <p className="font-medium text-red-500 text-sm">Rp{(bill.denda || 0).toLocaleString('id-ID')}</p>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-end pt-2 border-t border-slate-100">
                      <div className="text-xs text-slate-500 space-y-0.5">
                        <div>Harga Air: Rp{(bill.harga_air || 0).toLocaleString('id-ID')}</div>
                        <div>Adm: Rp{(bill.adm || 0).toLocaleString('id-ID')}</div>
                        <div className="text-sky-600 font-medium">Biaya Admin: Rp{(bill.admin_fee || 0).toLocaleString('id-ID')}</div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400">Subtotal</p>
                        <p className="text-lg font-bold text-sky-600">
                          Rp{(bill.total || 0).toLocaleString('id-ID')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Total Section */}
                <div className="bg-sky-50 border-2 border-sky-200 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-slate-800">Total Bayar</span>
                    <span className="text-xl font-bold text-sky-600">
                      Rp{payment.total_bayar.toLocaleString('id-ID')}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Sudah termasuk biaya admin
                  </p>
                </div>
              </div>
              
              {/* Footer */}
              <div className="text-center py-4 border-t border-slate-100 bg-white">
                <p className="text-xs text-slate-500">Terima kasih atas pembayaran Anda</p>
                <p className="text-xs text-slate-400">Struk ini adalah bukti pembayaran yang sah</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Primary: Print via RawBT (Bluetooth) */}
              <Button
                onClick={handlePrintRawBT}
                className="w-full h-14 gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl"
                data-testid="print-rawbt-btn"
              >
                <Printer className="w-5 h-5" />
                Print Struk (Bluetooth)
              </Button>
              
              {/* Secondary buttons */}
              <div className="flex gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={handleDownloadReceipt}
                  className="gap-2 flex-1"
                  data-testid="download-receipt-btn"
                >
                  <Download className="w-4 h-4" />
                  Unduh PNG
                </Button>
                <Button
                  variant="outline"
                  onClick={handlePrintReceipt}
                  className="gap-2 flex-1"
                  data-testid="print-receipt-btn"
                >
                  <Printer className="w-4 h-4" />
                  Print Biasa
                </Button>
              </div>
            </div>
          </div>
        );
      
      case "rejected":
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">
              Pembayaran Ditolak
            </h2>
            <p className="text-slate-600 mb-6">
              Maaf, pembayaran Anda tidak dapat diverifikasi. Silakan hubungi admin atau coba lagi.
            </p>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen hero-gradient">
      {/* Header */}
      <header className="container mx-auto px-6 py-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/icons/icon-72x72.png" alt="Logo" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-heading font-semibold text-slate-800">Produk Digital</span>
          </div>
        </nav>
      </header>

      <main className="container mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="max-w-md mx-auto">
            <Card className="glass-card overflow-hidden">
              <CardContent className="p-8">
                {isLoading ? (
                  <div className="text-center py-8">
                    <div className="spinner mx-auto mb-4" />
                    <p className="text-slate-600">Memuat status pembayaran...</p>
                  </div>
                ) : payment ? (
                  <StatusDisplay />
                ) : (
                  <div className="text-center py-8">
                    <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                    <p className="text-slate-600">Pembayaran tidak ditemukan</p>
                  </div>
                )}

                <div className="flex gap-3 mt-8">
                  <Button
                    variant="outline"
                    onClick={fetchPaymentStatus}
                    className="flex-1 gap-2"
                    data-testid="refresh-status-btn"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                  </Button>
                  <Link to="/" className="flex-1">
                    <Button className="w-full btn-flow gap-2" data-testid="back-home-btn">
                      <Home className="w-4 h-4" />
                      Beranda
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
